import { Fragment, useEffect, useRef, useState } from "react"
import { Listbox, Transition } from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/16/solid"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import { chatbotActions } from "~/ui-rtk/app/slices/chatbot.slice"
import { getPersonaIcon } from "./personaConfig"

interface Persona {
  id: string
  name: string
  description: string
}

const FALLBACK_PERSONAS: Persona[] = [
  { id: "cmo", name: "CMO", description: "Strategic overview" },
  { id: "growth", name: "Growth", description: "Paid media efficiency" },
  { id: "paidmedia", name: "Paid Media", description: "Ad optimization" },
  { id: "ecom", name: "E-commerce", description: "Product performance" },
  { id: "brand", name: "Brand", description: "Brand value tracking" },
  { id: "cro", name: "CRO", description: "Funnel optimization" },
  { id: "retention", name: "Retention", description: "Cohort & LTV" },
]

function PersonaOptionIcon({ personaId }: { personaId: string }) {
  const icon = getPersonaIcon(personaId)
  return (
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
      style={{ backgroundColor: `${icon.color}20` }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke={icon.color}
        className="w-3.5 h-3.5"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={icon.path} />
      </svg>
    </div>
  )
}

export function PersonaSelector({ disabled }: { disabled?: boolean }) {
  const dispatch = useAppDispatch()
  const selectedPersona = useAppSelector(
    state => state.chatbotSlice.selectedPersona,
  )
  const [personas, setPersonas] = useState<Persona[]>(FALLBACK_PERSONAS)

  // Ref avoids stale closure over selectedPersona without re-running the fetch
  const selectedPersonaRef = useRef(selectedPersona)
  selectedPersonaRef.current = selectedPersona

  useEffect(() => {
    let cancelled = false
    fetch(`${import.meta.env.VITE_API_BASE_URL || ""}/chatbot/personas`, {
      credentials: "include",
    })
      .then(res => res.json())
      .then((data: Persona[] | null) => {
        if (!cancelled && data && data.length > 0) {
          setPersonas(data)
          if (!data.some(p => p.id === selectedPersonaRef.current)) {
            dispatch(chatbotActions.setPersona(data[0].id))
          }
        }
      })
      .catch(() => {
        // Keep fallback personas
      })
    return () => {
      cancelled = true
    }
  }, [dispatch])

  const selected = personas.find(p => p.id === selectedPersona) ?? personas[0]
  const selectedIcon = getPersonaIcon(selectedPersona)

  return (
    <Listbox
      value={selectedPersona}
      disabled={disabled}
      onChange={(value: string) => dispatch(chatbotActions.setPersona(value))}
    >
      <div className="relative">
        <Listbox.Button className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium hover:bg-system-blue-600 transition-colors cursor-pointer">
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${selectedIcon.color}20` }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke={selectedIcon.color}
              className="w-3 h-3"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={selectedIcon.path}
              />
            </svg>
          </div>
          <span>{selected?.name}</span>
          <ChevronDownIcon className="w-3.5 h-3.5 opacity-50" aria-hidden />
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute left-0 top-full mt-1 z-50 w-64 rounded-xl border border-system-blue-500 bg-system-blue-700 p-1 shadow-xl focus:outline-none">
            {personas.map(persona => (
              <Listbox.Option
                key={persona.id}
                value={persona.id}
                className="group flex items-center gap-2.5 rounded-lg py-2 px-3 cursor-pointer data-[focus]:bg-system-blue-600 select-none"
              >
                {({ selected: isSelected }) => (
                  <Fragment>
                    <PersonaOptionIcon personaId={persona.id} />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span
                        className={`text-sm font-medium ${isSelected ? "text-brand" : ""}`}
                      >
                        {persona.name}
                      </span>
                      <span className="text-xs opacity-50 truncate">
                        {persona.description}
                      </span>
                    </div>
                    {isSelected && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-4 h-4 text-brand shrink-0"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </Fragment>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}
